import * as React from "react";
import { GeneratedAnswerInterface, APIOutput } from "@amzn/ask-legal-domain";
import { AppContext } from "../setup/context";

interface RealStreamingState {
    isLoading: boolean;
    content: string;
    citations: GeneratedAnswerInterface.Citation[];
    metadata: GeneratedAnswerInterface.SummaryMetadata | null;
    error: string | null;
    isComplete: boolean;
    thinkingMessage: string;
}

export const useSearchStreaming = () => {
    const context = React.useContext(AppContext);
    const [state, setState] = React.useState<RealStreamingState>({
        isLoading: false,
        content: "",
        citations: [],
        metadata: null,
        error: null,
        isComplete: false,
        thinkingMessage: ""
    });
    const requestInProgress = React.useRef(false);

    // Fallback to regular API when streaming fails
    const fallbackToGenerateAnswerAPI = React.useCallback(async (
        request: GeneratedAnswerInterface.GeneratedAnswerRequest
    ) => {
        try {
            const response = await context.getGeneratedAnswerAPI().generateAnswer(request);
            const output = APIOutput.fromRaw<GeneratedAnswerInterface.GeneratedAnswerResponse>(response.data);

            if (!output.isErr() && output.data.summary) {
                const summary = output.data.summary;

                setState(prevState => ({
                    ...prevState,
                    citations: summary.citations,
                    content: summary.content,
                    metadata: summary.metadata,
                    isLoading: false,
                    isComplete: true,
                    thinkingMessage: "",
                    error: null
                }));
            } else {
                setState(prevState => ({
                    ...prevState,
                    error: output.isErr() ? output.err.message : "No summary data received",
                    isLoading: false,
                    isComplete: true,
                    thinkingMessage: ""
                }));
            }
        } catch (error) {
            console.error("Error in generate summary API:", error);
            setState(prevState => ({
                ...prevState,
                error: error instanceof Error ? error.message : "Unknown error occurred",
                isLoading: false,
                thinkingMessage: ""
            }));
        }
    }, [context]);

    const generateRealStreamingAnswer = React.useCallback(async (
        request: GeneratedAnswerInterface.GeneratedAnswerRequest
    ) => {
        if (requestInProgress.current) {
            return;
        }

        requestInProgress.current = true;

        setState({
            isLoading: true,
            content: "",
            citations: [],
            metadata: null,
            error: null,
            isComplete: false,
            thinkingMessage: ""
        });

        try {
            const stream = await context.getGeneratedAnswerAPI().generateAnswerStream(request);
            const reader = stream.getReader();

            let hasCompletedSuccessfully = false;
            while (true) {
                const { done, value } = await reader.read();

                if (done) {
                    // If stream completed without successful completion, fallback to generate summary API
                    if (!hasCompletedSuccessfully) {
                        setState(prevState => ({
                            ...prevState,
                            error: null,
                            isLoading: true,
                            isComplete: false,
                            thinkingMessage: "Generating Search Summary..."
                        }));
                        await fallbackToGenerateAnswerAPI(request);
                    }
                    break;
                }

                if (value) {
                    setState(prevState => {
                        const newState = { ...prevState };
                        switch (value.type) {
                            case "thinking":
                                newState.thinkingMessage = value.data as string;
                                break;
                            case "content_start":
                                newState.thinkingMessage = value.data as string;
                                break;
                            case "content":
                                newState.content = (value.data as any).content;
                                newState.thinkingMessage = "";
                                break;
                            case "complete":
                                hasCompletedSuccessfully = true;
                                newState.isLoading = false;
                                newState.isComplete = true;
                                newState.content = (value.data as any).content;
                                newState.citations = (value.data as any).citations;
                                newState.metadata = (value.data as any).metadata;
                                newState.thinkingMessage = "";
                                break;
                            case "error":
                                newState.error = (value.data as any).message;
                                newState.isLoading = false;
                                newState.thinkingMessage = "";
                                break;
                        }

                        return newState;
                    });
                } else {
                    console.warn("Received null/undefined value from stream");
                }
            }
        } catch (error) {
            console.error("Error in generating streaming summary, falling back to generate summary API:", error);
            // Fallback to summary API when streaming fails
            await fallbackToGenerateAnswerAPI(request);
        } finally {
            requestInProgress.current = false;
        }
    }, [context, fallbackToGenerateAnswerAPI]);

    const reset = React.useCallback(() => {
        requestInProgress.current = false; // Reset the request flag
        setState({
            isLoading: false,
            content: "",
            citations: [],
            metadata: null,
            error: null,
            isComplete: false,
            thinkingMessage: ""
        });
    }, []);

    return {
        ...state,
        generateRealStreamingAnswer,
        reset
    };
};
